import React, { useState, useRef, useCallback } from 'react';
import { Icon } from './ui/Icon.jsx';
import imageCompression from 'browser-image-compression';
import { SUPABASE_URL, SUPABASE_KEY } from '../lib/constants.js';
import { getValidToken } from '../lib/supabase.js';
import { CACHE_CONTROL } from '../hooks/useImageUpload.js';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const BUCKET    = 'product-images';
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT    = ['image/jpeg','image/jpg','image/png','image/webp',
                   'image/gif','image/avif','image/heic','image/heif'];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function sanitizeFilename(rawName) {
  const slug = (rawName || 'product')
    .toLowerCase().trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9\u0600-\u06ff-]/g, '')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'product';
  return `${slug}-${Date.now()}-${Math.random().toString(36).slice(2,6)}.webp`;
}

// Remove extension, use filename stem as hint
function stemHint(filename) {
  return filename.replace(/\.[^.]+$/, '');
}

// ─── PER-FILE STATUS ──────────────────────────────────────────────────────────
// status: 'queued' | 'compressing' | 'uploading' | 'done' | 'error'
function makeEntry(file, index) {
  return { id: index + '-' + file.name, name: file.name, file,
           status: 'queued', progress: 0, url: '', error: '' };
}

// ─── LOW-LEVEL UPLOAD ─────────────────────────────────────────────────────────
// Uploads a single file; calls onProgress(pct), onDone(url), onError(msg).
// Every failure path logs the raw Supabase response so you can diagnose
// CORS / RLS / bucket-missing issues without opening DevTools Network tab.
async function uploadOne(file, onProgress, onDone, onError) {
  const tag = `[BulkUpload] ${file.name}`;

  if (file.size > MAX_BYTES) {
    const msg = `Too large (${(file.size/1024/1024).toFixed(1)} MB — max 10 MB)`;
    console.error(`${tag} — size guard:`, msg);
    onError(msg);
    return;
  }

  // ── Step 1: Compress ──────────────────────────────────────────────────────
  onProgress(5);
  let blob;
  try {
    blob = await imageCompression(file, {
      maxWidthOrHeight: 1400, initialQuality: 0.8,
      fileType: 'image/webp', useWebWorker: false,
      onProgress: p => onProgress(5 + Math.floor(p * 0.35)),
    });
  } catch (err1) {
    console.warn(`${tag} — WebP compression failed, retrying without fileType:`, err1);
    try {
      blob = await imageCompression(file, {
        maxWidthOrHeight: 1400, initialQuality: 0.8, useWebWorker: false,
      });
    } catch (err2) {
      const msg = 'Compression failed: ' + (err2.message || String(err2));
      console.error(`${tag} — compression fallback also failed:`, err2);
      onError(msg);
      return;
    }
  }

  // ── Step 2: Upload via XHR ────────────────────────────────────────────────
  onProgress(42);
  const filename  = sanitizeFilename(stemHint(file.name));
  const mimeType  = blob.type || 'image/webp';
  const token     = (await getValidToken()) || SUPABASE_KEY;
  const uploadURL = `${SUPABASE_URL}/storage/v1/object/${BUCKET}/${filename}`;

  // Log the target URL (omit token value for security)

  return new Promise(resolve => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadURL, true);
    xhr.setRequestHeader('apikey',        SUPABASE_KEY);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type',  mimeType);
    xhr.setRequestHeader('x-upsert',      'true');
    // Without this Supabase stores `no-cache`, defeating browser caching entirely.
    xhr.setRequestHeader('cache-control', CACHE_CONTROL);
    xhr.timeout = 60_000;

    xhr.upload.addEventListener('progress', evt => {
      if (evt.lengthComputable)
        onProgress(42 + Math.floor((evt.loaded / evt.total) * 55));
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const url = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${filename}`;
        onDone(url); resolve();
      } else {
        // ── HTTP error (403 RLS, 404 bucket, 413 too large, etc.) ──────────
        let parsed = null;
        try { parsed = JSON.parse(xhr.responseText); } catch {}
        const msg = parsed?.error || parsed?.message || `HTTP ${xhr.status}`;
        console.error(
          `${tag} — upload failed:`,
          `\n  status : ${xhr.status} ${xhr.statusText}`,
          `\n  body   : ${xhr.responseText}`,
          `\n  parsed :`, parsed,
        );
        onError(`${msg} (${xhr.status})`); resolve();
      }
    });

    xhr.addEventListener('error', () => {
      // Fires for network-level failures: CORS block, DNS failure, no internet.
      // If CORS is misconfigured you'll see no HTTP status here — the browser
      // blocks the preflight before the request even leaves the browser.
      console.error(
        `${tag} — XHR network error (likely CORS or no network).`,
        `\n  Check: Supabase Storage → Settings → CORS — allow origin must include this app's domain.`,
        `\n  Also confirm bucket "${BUCKET}" exists and is public.`,
        `\n  uploadURL was: ${uploadURL}`,
      );
      onError('Network error — check console for CORS/bucket details');
      resolve();
    });

    xhr.addEventListener('timeout', () => {
      console.error(`${tag} — XHR timed out after 60 s. uploadURL: ${uploadURL}`);
      onError('Timed out (60 s) — check console for details');
      resolve();
    });

    xhr.send(blob);
  });
}

// Small pause between files — avoids hammering Supabase on large batches
const sleep = ms => new Promise(r => setTimeout(r, ms));

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function BulkImageUploader({ lang }) {
  const ar = lang === 'ar';
  const [entries,  setEntries]  = useState([]);
  const [dragging, setDragging] = useState(false);
  const [copied,   setCopied]   = useState(false);
  const fileRef = useRef(null);

  // ── update a single entry by id ────────────────────────────────────────────
  const patch = useCallback((id, delta) =>
    setEntries(prev => prev.map(e => e.id === id ? { ...e, ...delta } : e)),
  []);

  // ── enqueue + start uploads ─────────────────────────────────────────────────
  const enqueue = useCallback(async (files) => {
    const valid = [...files].filter(f => ACCEPT.includes(f.type));
    if (valid.length === 0) return;

    const startIdx = Date.now(); // unique base for IDs
    const newEntries = valid.map((f, i) => makeEntry(f, startIdx + i));
    setEntries(prev => [...prev, ...newEntries]);

    // Upload strictly one-at-a-time with a short pause between files.
    // Sequential processing prevents browser connection-limit stalls and
    // Supabase rate-limit rejections on large batches.
    for (let i = 0; i < newEntries.length; i++) {
      const { id, file } = newEntries[i];
      patch(id, { status: 'compressing' });
      await uploadOne(
        file,
        pct => patch(id, { progress: pct,
                            status: pct < 42 ? 'compressing' : 'uploading' }),
        url => patch(id, { status: 'done',  progress: 100, url }),
        msg => patch(id, { status: 'error', error: msg }),
      );
      // 400 ms cooldown between files — skip after the last one
      if (i < newEntries.length - 1) await sleep(400);
    }
  }, [patch]);

  // ── drag & drop ─────────────────────────────────────────────────────────────
  const onDragOver  = e => { e.preventDefault(); setDragging(true);  };
  const onDragLeave = ()  => setDragging(false);
  const onDrop      = e  => {
    e.preventDefault(); setDragging(false);
    enqueue(e.dataTransfer.files);
  };
  const onFileInput = e => { enqueue(e.target.files); e.target.value = ''; };

  // ── copy helpers ─────────────────────────────────────────────────────────────
  const copyOne = (url) => navigator.clipboard?.writeText(url);

  const copyAll = () => {
    const urls = entries.filter(e => e.status === 'done').map(e => e.url).join('\n');
    navigator.clipboard?.writeText(urls).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const clearDone = () => setEntries(prev => prev.filter(e => e.status !== 'done'));

  const doneCount = entries.filter(e => e.status === 'done').length;
  const busyCount = entries.filter(e => e.status === 'compressing' || e.status === 'uploading').length;

  // ── styles ───────────────────────────────────────────────────────────────────
  const zoneStyle = {
    border:       `2px dashed ${dragging ? 'var(--sedra-forest-soft)' : '#d4d0c8'}`,
    borderRadius: 12,
    padding:      '28px 20px',
    textAlign:    'center',
    cursor:       'pointer',
    background:   dragging ? 'var(--sedra-sage-wash)' : 'var(--sedra-sage-wash)',
    transition:   'border-color 0.2s, background 0.2s',
    userSelect:   'none',
  };

  const phaseColor = s => s === 'done' ? '#166534' : s === 'error' ? 'var(--status-danger)' :
                          s === 'uploading' ? '#1d4ed8' : 'var(--status-warn-dark)'; // compressing
  const phaseBg    = s => s === 'done' ? 'var(--status-success-bg)' : s === 'error' ? 'var(--status-danger-bg)' :
                          s === 'uploading' ? '#dbeafe' : 'var(--status-warn-bg)';
  const phaseLabel = (s, err) => {
    if (s === 'done')        return ar ? 'تم الرفع' : 'Uploaded';
    if (s === 'error')       return (ar ? 'خطأ: ' : 'Error: ') + err;
    if (s === 'uploading')   return ar ? 'جاري الرفع…' : 'Uploading…';
    if (s === 'compressing') return ar ? 'ضغط…' : 'Compressing…';
    return ar ? 'في الانتظار' : 'Queued';
  };

  return (
    <div style={{ direction: ar ? 'rtl' : 'ltr' }}>

      {/* ── Drop zone ── */}
      <div
        style={zoneStyle}
        onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        role="button" tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && fileRef.current?.click()}
        aria-label={ar ? 'رفع صور متعددة' : 'Upload multiple images'}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: 'var(--sedra-sage-ink)' }}><Icon name="Image" size={36} /></div>
        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--sedra-forest-soft)', marginBottom: 4 }}>
          {ar ? 'اسحب صورك هنا أو انقر للاختيار' : 'Drag images here or click to select'}
        </div>
        <div style={{ fontSize: 12, color: '#aaa' }}>
          {ar
            ? 'يمكنك تحديد صور متعددة في نفس الوقت · JPEG / PNG / WebP · حد أقصى 10 ميغابايت'
            : 'Select multiple at once · JPEG / PNG / WebP · max 10 MB each'}
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple
          style={{ display: 'none' }} onChange={onFileInput} />
      </div>

      {/* ── File list ── */}
      {entries.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>

          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 13, color: '#666' }}>
              {busyCount > 0
                ? (ar ? `جاري رفع ${busyCount} صورة…` : `Uploading ${busyCount} image${busyCount > 1 ? 's' : ''}…`)
                : (ar ? `${doneCount} صورة جاهزة` : `${doneCount} image${doneCount !== 1 ? 's' : ''} ready`)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {doneCount > 0 && (
                <>
                  <button type="button" onClick={copyAll}
                    style={{ padding: '6px 14px', borderRadius: 7, background: copied ? 'var(--sedra-forest-soft)' : 'var(--sedra-sage-ink)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, transition: 'background 0.2s', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Icon name={copied ? 'Check' : 'ClipboardList'} size={12} />
                    {copied
                      ? (ar ? 'تم النسخ!' : 'Copied!')
                      : (ar ? `نسخ كل الروابط (${doneCount})` : `Copy All URLs (${doneCount})`)}
                  </button>
                  <button type="button" onClick={clearDone}
                    style={{ padding: '6px 12px', borderRadius: 7, background: 'var(--sedra-line)', color: '#666', border: 'none', cursor: 'pointer', fontSize: 12 }}>
                    {ar ? 'مسح المكتملة' : 'Clear done'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Rows */}
          {entries.map(e => (
            <div key={e.id} style={{
              background: '#fff', borderRadius: 10, padding: '10px 14px',
              boxShadow: '0 1px 5px rgba(0,0,0,0.07)',
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              {/* Thumbnail or placeholder */}
              {e.url
                ? <img src={e.url} alt="" style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 7, flexShrink: 0, border: '1px solid var(--sedra-line)' }} />
                : <div style={{ width: 46, height: 46, borderRadius: 7, background: '#f0ede8', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}><Icon name="Image" size={18} /></div>
              }

              {/* File name + status */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.name}
                </div>
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {/* Status badge — for errors show just the label, detail goes below */}
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, flexShrink: 0,
                    background: phaseBg(e.status), color: phaseColor(e.status) }}>
                    {e.status === 'error'
                      ? (ar ? 'فشل الرفع' : 'Upload failed')
                      : phaseLabel(e.status, e.error)}
                  </span>
                  {/* Progress bar */}
                  {(e.status === 'compressing' || e.status === 'uploading') && (
                    <div style={{ flex: 1, minWidth: 80, height: 5, borderRadius: 4, background: 'var(--sedra-line)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 4, background: 'var(--sedra-sage-ink)', width: `${e.progress}%`, transition: 'width 0.25s ease' }} />
                    </div>
                  )}
                </div>
                {/* Error detail — full text, monospace, so HTTP codes / CORS hints are readable */}
                {e.status === 'error' && (
                  <div style={{ marginTop: 4, fontSize: 11, color: 'var(--status-danger)', fontFamily: 'monospace',
                    background: 'var(--status-danger-bg)', border: '1px solid var(--status-danger-bg)', borderRadius: 6,
                    padding: '4px 8px', lineHeight: 1.5, wordBreak: 'break-all' }}>
                    {e.error}
                    <span style={{ marginLeft: 8, color: '#aaa', fontFamily: 'sans-serif' }}>
                      {ar ? '(راجع console للتفاصيل)' : '(see browser console for full details)'}
                    </span>
                  </div>
                )}
                {/* URL display */}
                {e.status === 'done' && (
                  <div style={{ marginTop: 5, fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                    {e.url}
                  </div>
                )}
              </div>

              {/* Copy button */}
              {e.status === 'done' && (
                <CopyButton url={e.url} ar={ar} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state hint */}
      {entries.length === 0 && (
        <div style={{ marginTop: 16, padding: '14px 18px', borderRadius: 10, background: '#F7F5F0', border: '1px solid #ebe7df', fontSize: 13, color: '#888', lineHeight: 1.7, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <Icon name="Lightbulb" size={15} style={{ flexShrink: 0, marginTop: 2 }} />
          {ar
            ? 'ارفع صورك هنا، ثم انسخ الروابط الناتجة وضعها في عمود image_url في ملف CSV الخاص بك.'
            : 'Upload your images here, then copy the generated URLs into the image_url column of your CSV sheet.'}
        </div>
      )}
    </div>
  );
}

// ─── COPY BUTTON (manages its own "copied" flash) ─────────────────────────────
function CopyButton({ url, ar }) {
  const [flash, setFlash] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(url).then(() => {
      setFlash(true); setTimeout(() => setFlash(false), 1600);
    });
  };
  return (
    <button type="button" onClick={copy}
      style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 7,
        background: flash ? 'var(--sedra-forest-soft)' : '#F0EDE7', color: flash ? '#fff' : 'var(--sedra-forest-soft)',
        border: '1.5px solid ' + (flash ? 'var(--sedra-forest-soft)' : '#d4cfc8'),
        cursor: 'pointer', fontSize: 12, fontWeight: 700, transition: 'background 0.2s, color 0.2s',
        whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <Icon name={flash ? 'Check' : 'ClipboardList'} size={12} /> {flash ? (ar ? 'تم' : 'Copied') : (ar ? 'نسخ' : 'Copy')}
    </button>
  );
}
